// @owners { team: patients }
import { ReactNativeBiometrics } from './biometrics';
import { getCompanyFullName } from '~shared/config/getCompanyName';
import { BIOMETRIC_EXPIRY_TIME } from '~shared/constants';
import { type ApiOptions } from '~shared/helpers/api';
import { post } from '~shared/helpers/apiHelper';
import getDeviceID from '~shared/helpers/getDeviceID';
import { Storage } from '~shared/mmkv';

type RNBiometrics = {
  isSensorAvailable: () => Promise<AvailableBiometricsResponse>;
  createKeys: () => Promise<CreateKeypairResponse>;
  createSignature: (params: { promptMessage: string; payload: string }) => Promise<CreateSignatureResponse>;
  simplePrompt: (params: { promptMessage: string }) => Promise<{ success: boolean; error?: string }>;
};

// react-native-biometrics v2+ exports a class that must be instantiated.
// (The web stub from ./biometrics is an object literal, so we only `new` on native.)
const rnBiometrics = (
  typeof ReactNativeBiometrics === 'function'
    ? new (ReactNativeBiometrics as unknown as new () => unknown)()
    : ReactNativeBiometrics
) as RNBiometrics;

const V3_API_VERSION = 'v3';
const v3Options: ApiOptions = {
  version: V3_API_VERSION,
};

async function savePublicKey(userID: number, publicKey: string) {
  const deviceID = getDeviceID();
  return post(
    `/users/${userID}/biometric/${deviceID}`,
    {
      public_key: publicKey,
    },
    v3Options,
  );
}

async function upgradeSession(userID: number) {
  const deviceID = getDeviceID();
  return post(`/users/${userID}/biometric/${deviceID}/upgrade_session`, {}, v3Options);
}

// Creates and saves biometric keypair, then upgrades session length and saves biometric session cookies
export async function configureBiometricSession(currentUserID: number) {
  const keypairResponse = await createKeypair();

  if (!keypairResponse.publicKey) {
    return false;
  }

  const res = await savePublicKey(currentUserID, keypairResponse.publicKey);

  if (res.error) {
    return false;
  }

  const upgradeResponse = await upgradeSession(currentUserID);

  if (upgradeResponse.error) {
    return false;
  }

  Storage.set(BIOMETRIC_EXPIRY_TIME, upgradeResponse.expires_at);
  return true;
}

export type AvailableBiometricsResponse = {
  available: boolean;
  biometryType: string | undefined;
  error?: string;
};

export const availableBiometrics = async (): Promise<AvailableBiometricsResponse> => {
  return rnBiometrics.isSensorAvailable();
};

export type CreateKeypairResponse = {
  publicKey: string;
};

export const createKeypair = async (): Promise<CreateKeypairResponse> => {
  return rnBiometrics.createKeys();
};

export type CreateSignatureResponse = {
  success: boolean;
  signature: string;
  error?: string;
};

export const BIOMETRIC_SIGNATURE_PAYLOAD = 'Verify your face or fingerprint to unlock';
export const createSignature = async (): Promise<CreateSignatureResponse> => {
  return rnBiometrics.createSignature({
    promptMessage: `Face or fingerprint ID for ${getCompanyFullName()}`,
    payload: BIOMETRIC_SIGNATURE_PAYLOAD,
  });
};

export const promptBiometrics = async () => {
  return rnBiometrics.simplePrompt({
    promptMessage: 'Confirm ID',
  });
};
